import { useDebouncedValue } from '@mantine/hooks'
import { getConnectedEdges, useEdges, useReactFlow } from '@xyflow/react'
import { useMemo } from 'react'
import type { RBusinessHours } from 'v2/types/api/settings'
import { outputStates } from '../config/nodes.config'
import { NO_REPLY_HANDLE } from '../constants'
import { isTextAgentChannel } from '../run-info/types'
import { FlowNodeTypes, type GenericNode, type NodeDataBase, type OutputState } from '../types'
import { mapButtonsToOutputStates } from './ai-agent-utils'
import { isAiAgentV2Type } from './ai-agent-v29'

interface GetOutputStatesOptions {
  data: NodeConfig
  type: FlowNodeTypes
}

interface BusinessHourConfig {
  business_hrs: RBusinessHours[]
}

interface BranchV2Config {
  conditions: Array<{ alias: string }>
}

interface AiAgentConfig {
  node_config: {
    intents: Array<{ intent_name?: string }>
  }
  conversation_type: 'preset_message' | 'ai_generated'
  wait_for_response: boolean
  disable_human_handoff: boolean
  disable_inequipped_to_serve: boolean
  buttons?: Array<{
    text?: string
    title?: string
    button_type?: string
  }>
}

interface AiAgentV2Config {
  intents: Array<{ id: string; intent_name?: string }>
  channel?: string
  wait_for_response?: boolean
}

interface AiActionConfig {
  intents: Array<{ id: string; intent_name?: string }>
}

interface InitiateCallConfig {
  detect_voicemail: boolean
}

interface SendMessageConfig {
  wait_for_response: boolean
}

interface IVRMenuConfig {
  allowed_inputs?: string[]
}

type NodeConfig = {
  name: string
  payload_format?: string | Record<string, unknown>
} & Partial<BusinessHourConfig> &
  Partial<BranchV2Config> &
  Partial<AiAgentConfig> &
  Partial<AiAgentV2Config> &
  Partial<AiActionConfig> &
  Partial<InitiateCallConfig> &
  Partial<SendMessageConfig> &
  Partial<IVRMenuConfig>

export const getOutputStates = ({ data, type }: GetOutputStatesOptions): OutputState[] => {
  if (!data) return outputStates[type]
  switch (type) {
    case FlowNodeTypes.business_hour: {
      return [
        ...(outputStates[type] as OutputState[]),
        ...(data.business_hrs || []).map((bh: RBusinessHours) => ({
          ...bh,
          default: false,
          id: bh.name,
        })),
      ]
    }

    case FlowNodeTypes.branch_v2: {
      const branchOutputStates = (data.conditions || []).map((condition) => ({
        default: false,
        id: condition.alias,
        name: condition.alias,
      }))
      return [...branchOutputStates, ...outputStates[type]]
    }

    case FlowNodeTypes.ai_agent_v29:
    case FlowNodeTypes.ai_agent_v2:
    case FlowNodeTypes.agent_node:
    case FlowNodeTypes.ai_action: {
      const intentsList = (data.intents || [])
        .map((intent) =>
          intent.intent_name && intent.id
            ? { id: intent.id, name: intent.intent_name, default: false }
            : null,
        )
        .filter(
          (intent): intent is { id: string; name: string; default: boolean } => intent !== null,
        )

      // A waiting chat, SMS or WhatsApp AI node also exposes its reply-timeout
      // exit: the backend advances the flow through "no reply" when the customer
      // stays silent past the configured timeout. Voice has no such exit.
      if (isAiAgentV2Type(type) && isTextAgentChannel(data.channel) && data.wait_for_response) {
        return [...intentsList, { id: NO_REPLY_HANDLE, name: 'No Reply', default: false }]
      }

      return intentsList
    }

    case FlowNodeTypes.stream: {
      // Stream has fixed output states: completed and error
      return outputStates[type] || []
    }

    case FlowNodeTypes.ai_agent_message:
    case FlowNodeTypes.ai_agent_chat:
    case FlowNodeTypes.ai_agent_whatsapp: {
      const nodeConfig = data.node_config
      const intentsList =
        nodeConfig?.intents
          .map((intent) =>
            intent.intent_name
              ? { id: intent.intent_name, name: intent.intent_name, default: false }
              : null,
          )
          .filter(
            (intent): intent is { id: string; name: string; default: boolean } => intent !== null,
          ) || []

      const allOutputStates = outputStates[type]
      const filteredOutputStates = allOutputStates.filter((state) => {
        const {
          conversation_type: conversationType,
          wait_for_response: waitForResponse,
          disable_human_handoff: disableHumanHandoff,
          disable_inequipped_to_serve: disableInequippedToServe,
        } = data

        if (state.id === 'human_handoff' && disableHumanHandoff) return false
        if (state.id === 'inequipped_to_serve' && disableInequippedToServe) return false

        if (conversationType === 'preset_message') {
          if (state.id === 'error') return true
          return state.meta?.wait === waitForResponse
        }

        if (conversationType === 'ai_generated') {
          return (state.meta?.conversation_type as string[])?.includes('ai_generated')
        }

        return false
      })

      // Add buttons list for WhatsApp and Chat nodes
      const buttonsList =
        type !== FlowNodeTypes.ai_agent_message ? mapButtonsToOutputStates(data.buttons) : []

      return [...intentsList, ...filteredOutputStates, ...buttonsList]
    }

    case FlowNodeTypes.initiate_call: {
      return outputStates[type].filter((state) => data.detect_voicemail || state.default)
    }

    case FlowNodeTypes.send_message: {
      return outputStates[type].filter((state) => {
        if (data.wait_for_response === true) {
          return state.meta?.wait === true || state.id === 'failed'
        }
        return state.meta?.wait === false
      })
    }

    case FlowNodeTypes.agent_preset_conversation: {
      const filteredOutputStates = outputStates[type].filter((state) => {
        if (data.wait_for_response === true) {
          return state.meta?.wait === true || state.id === 'failed'
        }
        return state.meta?.wait === false
      })

      // Add buttons list for agent_preset_conversation node similar to ai_agent_whatsapp and ai_agent_chat
      const buttonsList = mapButtonsToOutputStates(data.buttons)

      return [...filteredOutputStates, ...buttonsList]
    }

    case FlowNodeTypes.ivr_menu: {
      return outputStates[type].filter((state) => {
        const outputState = state as OutputState
        return outputState.default || data.allowed_inputs?.includes(outputState.id)
      })
    }

    case FlowNodeTypes.app_action: {
      const isValid = data && Object.keys(data).length > 0
      if (!isValid) return []
      return Array.isArray(outputStates[type]) ? outputStates[type] : [outputStates[type]]
    }

    default:
      return outputStates[type] || []
  }
}

/**
 * Custom hook that returns debounced edges to prevent unnecessary re-renders
 * when edges change rapidly (e.g., during drag operations)
 */
export function useDebouncedEdges(delay = 500) {
  const edges = useEdges()
  const [debouncedEdges] = useDebouncedValue(edges, delay)
  return debouncedEdges
}

export function useOutputStates(id: string, n?: GenericNode) {
  const { getNode } = useReactFlow()
  const edges = useDebouncedEdges()

  const node = n || getNode(id)
  const nodeConfig = node?.data?.config as NodeDataBase['config'] | undefined
  const type = (node?.type as FlowNodeTypes) || FlowNodeTypes.start

  const opStates = useMemo(
    () => (nodeConfig ? getOutputStates({ data: nodeConfig, type }) : outputStates[type] || []),
    [nodeConfig, type],
  )

  // TODO: This is a performance bottleneck we should see how we can remove this
  const unConnectedOpStates = useMemo(() => {
    if (!node) return opStates
    if (!edges) return opStates
    const outGoers = getConnectedEdges([node], edges)
    if (!outGoers) return opStates
    if (isAiAgentV2Type(type) || type === FlowNodeTypes.ai_action) return opStates
    const unConnected = opStates.filter(
      (state) =>
        !outGoers.some((edge) => edge.sourceHandle === state.id && edge.source === node.id),
    )
    return unConnected
  }, [opStates, edges])

  return { opStates, unConnectedOpStates, node }
}
